// PositionsTable.jsx — Column definitions, cell rendering, and the positions data table
// Uses react-virtuoso TableVirtuoso for virtual scrolling (same pattern as MW DataTable)
// Built with Tailwind CSS (all new pages use Tailwind per styling decision 19 March 2026)

import React, { forwardRef, useMemo } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { fmtINR, fmtNum } from './calculations';
import CheckboxDropdown from './CheckboxDropdown';
import ResizeGrip from './ResizeGrip';

// ═══════════════════════════════════════════════════════════════
// COLUMN DEFINITIONS — 30 columns per Positions Page Spec
// ═══════════════════════════════════════════════════════════════

// Columns with filter: 'xxx' get a compact CheckboxDropdown icon in the header
export const POS_COLUMNS = [
  { key: 'underlying',           label: 'Underlying',    align: 'left',   width: 150 },
  { key: 'buyOrSell',            label: 'B/S',           align: 'center', width: 36,  filter: 'bs' },
  { key: 'publicPrivate',        label: 'Pub/Pvt',       align: 'center', width: 52,  filter: 'publicPrivate' },
  { key: 'sector',               label: 'Sector',        align: 'left',   width: 76,  filter: 'sector' },
  { key: 'category',             label: 'Category',      align: 'left',   width: 70,  filter: 'category' },
  { key: 'type',                 label: 'C/P',           align: 'center', width: 36,  filter: 'type' },
  { key: 'expiryStr',            label: 'Expiry',        align: 'left',   width: 78,  filter: 'expiry' },
  { key: 'strike',               label: 'Strike',        align: 'right',  width: 68 },
  { key: 'spot',                 label: 'CMP',           align: 'right',  width: 68 },
  { key: 'strikeStockRatio',     label: 'S/S%',          align: 'right',  width: 52 },
  { key: 'avgPrice',             label: 'Avg',           align: 'right',  width: 62 },
  { key: 'ltp',                  label: 'LTP',           align: 'right',  width: 62 },
  { key: 'percentage',           label: '%',             align: 'right',  width: 48 },
  { key: 'lotSize',              label: 'Lot',           align: 'right',  width: 40 },
  { key: 'lots',                 label: 'Lots',          align: 'right',  width: 40 },
  { key: 'margin',               label: 'Margin',        align: 'right',  width: 68 },
  { key: 'totalMargin',          label: 'Tot Margin',    align: 'right',  width: 76 },
  { key: 'totalPremium',         label: 'Premium',       align: 'right',  width: 72 },
  { key: 'remaining',            label: 'Remaining',     align: 'right',  width: 72 },
  { key: 'profit',               label: 'Profit',        align: 'right',  width: 72 },
  { key: 'returns',              label: 'Ret%',          align: 'right',  width: 48 },
  { key: 'returnsLeft',          label: 'RetL%',         align: 'right',  width: 48 },
  { key: 'annualizedReturn',     label: 'Ann%',          align: 'right',  width: 50 },
  { key: 'annualizedReturnsLeft',label: 'AnnL%',         align: 'right',  width: 50 },
  { key: 'bestBuySell',          label: 'Bid/Ask',       align: 'right',  width: 100 },
  { key: 'entryIV',              label: 'EIV%',          align: 'right',  width: 46 },
  { key: 'currentIV',            label: 'IV%',           align: 'right',  width: 46 },
  { key: 'volume',               label: 'Vol (Lots)',    align: 'right',  width: 66 },
  { key: 'oi',                   label: 'OI (Lots)',     align: 'right',  width: 66 },
  { key: 'accountLabel',         label: 'Account',       align: 'left',   width: 80,  filter: 'account' },
  { key: 'resultDate',           label: 'Result',        align: 'left',   width: 78 },
  // Col 30: Trade button is rendered separately (not in COLUMNS array)
];

// ═══════════════════════════════════════════════════════════════
// RENDER CELL — maps column key to the correct <td>
// ═══════════════════════════════════════════════════════════════

// Session 46 — Order badge renderer (matches MW DataTable's renderBadges)
// Orange pill: "S 100 PE". Max 2 visible + "+N" overflow with full list in hover tooltip.
// Rendered as a row of pills with no leading margin — caller places it below the name.
function renderOrderBadges(items) {
  if (!items || items.length === 0) return null;
  const visible = items.slice(0, 2);
  const overflow = items.length - visible.length;
  const tooltip = items.map(b => {
    const exp = b.expiryStr ? ` ${b.expiryStr}` : '';
    const acc = b.accountLabel ? ` (${b.accountLabel})` : '';
    return `${b.direction} ${b.strike || '?'} ${b.type || ''}${exp}${acc}`;
  }).join('\n');
  const pillStyle = {
    fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
    background: 'rgba(249,115,22,0.15)', color: '#fdba74',
    border: '1px solid rgba(249,115,22,0.35)', whiteSpace: 'nowrap', lineHeight: 1.2, flexShrink: 0
  };
  return (
    <span style={{ display: 'inline-flex', gap: 3, flexShrink: 0 }} title={tooltip}>
      {visible.map((b, i) => (
        <span key={i} style={pillStyle}>
          {b.direction}{' '}{b.strike || '?'}{' '}{b.type || ''}
        </span>
      ))}
      {overflow > 0 && (
        <span style={pillStyle}>+{overflow}</span>
      )}
    </span>
  );
}

function renderCell(col, row, isDark, extras) {
  const ex = extras || {};
  const ordersBySymbol = ex.ordersBySymbol; // Map<symbol, badges[]> or undefined

  // Shared inline styles (Tailwind handles most, but cell padding/alignment is per-cell)
  const base = 'px-1.5 py-1 whitespace-nowrap text-xs-tight';
  const muted = isDark ? 'text-app-text-muted' : 'text-app-text-light-muted';
  const dim = isDark ? 'text-app-text-dim' : 'text-app-text-light-dim';
  const text = isDark ? 'text-app-text' : 'text-app-text-light';

  switch (col.key) {
    case 'underlying': {
      // Session 46 — Underlying name on top, order badges BELOW (stacked).
      // Name always visible at full width. Badge row appears only if user has
      // an active order on this symbol (any account). Match by row.symbol (uppercased).
      const symKey = (row.symbol || '').toUpperCase();
      const ordBadges = ordersBySymbol ? ordersBySymbol.get(symKey) : null;
      const hasBadges = ordBadges && ordBadges.length > 0;
      return (
        <td key={col.key} className={`${base} font-semibold ${text}`} style={{ maxWidth: col.width, verticalAlign: 'top' }} title={row.underlying}>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {row.underlying}
          </div>
          {hasBadges && (
            <div style={{ marginTop: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              {renderOrderBadges(ordBadges)}
            </div>
          )}
        </td>
      );
    }
    case 'buyOrSell':
      return (
        <td key={col.key} className={`${base} text-center`}>
          <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${row.buyOrSell === 'BUY' ? (isDark ? 'bg-[#1a2e1a] text-accent-green' : 'bg-green-100 text-green-600') : (isDark ? 'bg-[#2e1a1a] text-accent-red' : 'bg-red-100 text-red-600')}`}>
            {row.buyOrSell}
          </span>
        </td>
      );
    case 'publicPrivate':
      return <td key={col.key} className={`${base} text-center ${muted}`}>{row.publicPrivate || '—'}</td>;
    case 'sector':
      return <td key={col.key} className={`${base} ${muted}`}>{row.sector || '—'}</td>;
    case 'category':
      return <td key={col.key} className={`${base} ${muted}`}>{row.category || '—'}</td>;
    case 'type':
      return (
        <td key={col.key} className={`${base} text-center`}>
          <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${row.type === 'CE' ? (isDark ? 'bg-[#1a2e1a] text-accent-green' : 'bg-green-100 text-green-600') : (isDark ? 'bg-[#2e1a1a] text-accent-red' : 'bg-red-100 text-red-600')}`}>
            {row.type}
          </span>
        </td>
      );
    case 'expiryStr':
      return <td key={col.key} className={`${base} ${muted}`}>{row.expiryStr}</td>;
    case 'strike':
      return <td key={col.key} className={`${base} text-right font-medium ${text}`}>{fmtNum(row.strike)}</td>;
    case 'spot':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.spot > 0 ? Number(row.spot).toFixed(2) : '—'}</td>;
    case 'strikeStockRatio':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.strikeStockRatio > 0 ? `${Number(row.strikeStockRatio).toFixed(2)}%` : '—'}</td>;
    case 'avgPrice':
      return <td key={col.key} className={`${base} text-right ${text}`}>{row.avgPrice > 0 ? `₹${Number(row.avgPrice).toFixed(2)}` : '—'}</td>;
    case 'ltp':
      return <td key={col.key} className={`${base} text-right font-semibold ${text}`}>{row.ltp > 0 ? `₹${Number(row.ltp).toFixed(2)}` : '—'}</td>;
    case 'percentage': {
      const pct = row.percentage;
      const color = pct === 0 ? dim : pct < 100 ? 'text-accent-green' : pct > 100 ? 'text-accent-red' : muted;
      return <td key={col.key} className={`${base} text-right ${color}`}>{pct > 0 ? `${pct}%` : '—'}</td>;
    }
    case 'lotSize':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.lotSize || '—'}</td>;
    case 'lots':
      return <td key={col.key} className={`${base} text-right font-medium ${text}`}>{row.lots}</td>;
    case 'margin':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.margin > 0 ? fmtINR(row.margin) : '—'}</td>;
    case 'totalMargin':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.totalMargin > 0 ? fmtINR(row.totalMargin) : '—'}</td>;
    case 'totalPremium':
      return <td key={col.key} className={`${base} text-right text-accent-green`}>{fmtINR(row.totalPremium)}</td>;
    case 'remaining':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{fmtINR(row.remaining)}</td>;
    case 'profit': {
      const profitColor = row.profit > 0 ? 'text-accent-green' : row.profit < 0 ? 'text-accent-red' : muted;
      return <td key={col.key} className={`${base} text-right font-semibold ${profitColor}`}>{fmtINR(row.profit)}</td>;
    }
    case 'returns':
      return <td key={col.key} className={`${base} text-right ${text}`}>{row.returns > 0 ? `${row.returns}%` : '—'}</td>;
    case 'returnsLeft':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.returnsLeft > 0 ? `${row.returnsLeft}%` : '—'}</td>;
    case 'annualizedReturn':
      return <td key={col.key} className={`${base} text-right ${row.annualizedReturn > 30 ? 'text-accent-yellow' : muted}`}>{row.annualizedReturn > 0 ? `${row.annualizedReturn}%` : '—'}</td>;
    case 'annualizedReturnsLeft':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.annualizedReturnsLeft > 0 ? `${row.annualizedReturnsLeft}%` : '—'}</td>;
    case 'bestBuySell': {
      const buy = row.bestBuyPrice || 0;
      const sell = row.bestSellPrice || 0;
      if (buy === 0 && sell === 0) return <td key={col.key} className={`${base} text-right ${dim}`}>—</td>;
      return (
        <td key={col.key} className={`${base} text-right`}>
          <span className="text-accent-green">{buy > 0 ? Number(buy).toFixed(2) : '—'}</span>
          <span className={dim}> / </span>
          <span className="text-accent-red">{sell > 0 ? Number(sell).toFixed(2) : '—'}</span>
        </td>
      );
    }
    case 'entryIV':
      return <td key={col.key} className={`${base} text-right text-accent-purple`}>{row.entryIV > 0 ? `${Number(row.entryIV).toFixed(1)}%` : '—'}</td>;
    case 'currentIV':
      return <td key={col.key} className={`${base} text-right text-accent-purple`}>{row.currentIV > 0 ? `${row.currentIV}%` : '—'}</td>;
    case 'volume':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{fmtNum(row.volume)}</td>;
    case 'oi':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{fmtNum(row.oi)}</td>;
    case 'accountLabel':
      return <td key={col.key} className={`${base} ${muted}`}>{row.accountLabel || row.account}</td>;
    case 'resultDate':
      return (
        <td key={col.key} className={`${base}`}>
          {row.resultDate ? (
            <span className="flex items-center gap-1">
              {row.resultsBeforeExpiry && (
                <span className="w-1.5 h-1.5 rounded-full bg-accent-red flex-shrink-0" title="Results before expiry" />
              )}
              <span className={row.isFutureResult ? 'text-accent-yellow' : muted}>{row.resultDate}</span>
            </span>
          ) : (
            <span className={dim}>—</span>
          )}
        </td>
      );
    default:
      return <td key={col.key} className={`${base} ${dim}`}>—</td>;
  }
}

// ═══════════════════════════════════════════════════════════════
// POSITIONS TABLE — Virtual Scrolling via TableVirtuoso
// ═══════════════════════════════════════════════════════════════

function PositionsTable({ rows, sortCol, sortDir, onSort, onTradeClick, isDark, filters = {}, t = {}, ordersBySymbol, columns = POS_COLUMNS, onColumnResize }) {
  // Session 46 — Bundle extras for renderCell (matches MW DataTable pattern).
  // Memoized so renderCell sees a stable object identity until orders change.
  const extras = useMemo(() => ({ ordersBySymbol }), [ordersBySymbol]);
  // Map filter keys to their state/options
  const filterMap = {
    expiry:        { selected: filters.expiryFilter        || [], setSelected: filters.setExpiryFilter,        options: filters.uniqueExpiries        || [] },
    type:          { selected: filters.typeFilter          || [], setSelected: filters.setTypeFilter,          options: ['All', 'CE', 'PE'] },
    bs:            { selected: filters.bsFilter            || [], setSelected: filters.setBsFilter,            options: ['All', 'BUY', 'SELL'] },
    publicPrivate: { selected: filters.publicPrivateFilter || [], setSelected: filters.setPublicPrivateFilter, options: filters.uniquePublicPrivate   || [] },
    sector:        { selected: filters.sectorFilter        || [], setSelected: filters.setSectorFilter,        options: filters.uniqueSectors         || [] },
    category:      { selected: filters.categoryFilter      || [], setSelected: filters.setCategoryFilter,      options: filters.uniqueCategories      || [] },
    account:       { selected: filters.accountFilter       || [], setSelected: filters.setAccountFilter,       options: filters.uniqueAccountLabels   || [] },
  };
  const borderColor = isDark ? '#1e2130' : '#e5e7eb';
  const headerBg = isDark ? '#13151e' : '#f9fafb';
  const headerText = isDark ? '#8b8fa3' : '#6b7280';
  const hoverBg = isDark ? '#1e2130' : '#f0f4ff';

  const tableComponents = useMemo(() => ({
    Table: forwardRef(({ style, ...props }, ref) => (
      <>
        <style>{`table.pos-vt tbody td { border-bottom: 1px solid ${borderColor}; }`}</style>
        <table ref={ref} {...props} className="pos-vt" style={{ ...style, width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, minWidth: 1800 }} />
      </>
    )),
    TableRow: forwardRef(({ style, ...props }, ref) => (
      <tr ref={ref} {...props} style={{ ...style, background: 'transparent' }}
        onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      />
    )),
  }), [borderColor, hoverBg]);

  if (rows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ color: headerText }}>
        <div className="text-center py-10">
          <div className="text-sm font-medium mb-1">No open positions</div>
          <div className="text-xs opacity-60">Positions will appear here when you have active F&O trades</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-x-auto flex flex-col min-h-0">
      <TableVirtuoso
        style={{ flex: 1, minHeight: 0 }}
        data={rows}
        // Track rows by stable identity (position id), not array index — keeps the row you're
        // watching anchored when the set changes underneath you (same fix as Market Watch).
        computeItemKey={(_, row) => row.id}
        components={tableComponents}
        overscan={10}
        fixedHeaderContent={() => (
          <tr style={{ background: headerBg }}>
            {columns.map(col => {
              const fm = col.filter ? filterMap[col.filter] : null;
              return (
                <th key={col.key}
                  style={{
                    padding: '5px 6px', textAlign: col.align, fontWeight: 600, fontSize: 10.5,
                    color: headerText, textTransform: 'uppercase', letterSpacing: 0.3,
                    cursor: 'pointer', userSelect: 'none', position: 'relative',
                    borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap',
                    width: col.width, minWidth: col.width,
                    background: sortCol === col.key ? (isDark ? '#1e2240' : '#e8ecff') : headerBg
                  }}
                >
                  <span onClick={() => onSort(col.key)}>{col.label}</span>
                  {sortCol === col.key && <span className="ml-0.5 text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                  {fm && fm.setSelected && (
                    <CheckboxDropdown
                      label={col.label}
                      selected={fm.selected}
                      setSelected={fm.setSelected}
                      options={fm.options}
                      t={t}
                      compact
                    />
                  )}
                  {onColumnResize && <ResizeGrip colKey={col.key} width={col.width} onResize={onColumnResize} />}
                </th>
              );
            })}
            <th style={{ padding: '5px 6px', width: 52, minWidth: 52, borderBottom: `1px solid ${borderColor}`, background: headerBg }} />
          </tr>
        )}
        itemContent={(index, row) => (
          <>
            {columns.map(col => renderCell(col, row, isDark, extras))}
            <td className="px-1.5 py-1 text-center">
              <button onClick={() => onTradeClick(row)}
                className={`px-2.5 py-0.5 text-[11px] font-semibold rounded cursor-pointer border-none ${isDark ? 'bg-[#1e2240] text-accent-blue' : 'bg-blue-50 text-blue-600'}`}
              >
                Trade
              </button>
            </td>
          </>
        )}
      />
    </div>
  );
}

export default PositionsTable;
